import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Mail, FileText, MessageCircle, Send,
  Loader2, Clock, User, Building2, Phone, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_PASSWORD = 'woconsulting2026';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(false);
  const [password, setPassword] = useState('');
  const [tab, setTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ contacts: 0, quotes: 0, chats: 0 });
  const chatEndRef = useRef(null);

  const login = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setAuthed(true);
      sessionStorage.setItem('wo_admin', '1');
    } else {
      toast.error('Mot de passe incorrect');
    }
  };

  useEffect(() => {
    if (sessionStorage.getItem('wo_admin') === '1') setAuthed(true);
  }, []);

  useEffect(() => {
    if (authed) fetchAll();
  }, [authed, tab]);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollTop = chatEndRef.current.scrollHeight;
  }, [chatMessages]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [q, c, s, st] = await Promise.all([
        axios.get(`${API_URL}/quotes`),
        axios.get(`${API_URL}/contacts`),
        axios.get(`${API_URL}/chat/sessions`),
        axios.get(`${API_URL}/admin/stats`),
      ]);
      setQuotes(q.data);
      setContacts(c.data);
      setChatSessions(s.data);
      setStats(st.data);
    } catch (e) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (session) => {
    setSelectedSession(session);
    try {
      const res = await axios.get(`${API_URL}/chat/messages/${session.session_id}`);
      setChatMessages(res.data);
    } catch (e) {
      toast.error('Erreur chargement messages');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedSession) return;
    try {
      await axios.post(`${API_URL}/chat/admin-reply`, {
        session_id: selectedSession.session_id,
        sender: 'admin',
        text: replyText.trim(),
      });
      setChatMessages(prev => [...prev, {
        sender: 'admin',
        text: replyText.trim(),
        created_at: new Date().toISOString(),
      }]);
      setReplyText('');
      toast.success('Réponse envoyée');
    } catch (e) {
      toast.error('Erreur envoi');
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-sm space-y-5">
          <div className="text-center">
            <h1 className="text-xl font-bold text-[#1a365d]">Admin WO Consulting</h1>
            <p className="text-sm text-gray-500 mt-1">Entrez le mot de passe pour accéder</p>
          </div>
          <Input
            data-testid="admin-password-input"
            type="password"
            placeholder="Mot de passe"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="h-11 rounded-lg"
            autoFocus
          />
          <Button
            data-testid="admin-login-btn"
            type="submit"
            className="w-full bg-[#1a365d] hover:bg-[#0f2440] text-white rounded-full h-11 font-semibold"
          >
            Connexion
          </Button>
          <Link to="/" className="block text-center text-sm text-gray-400 hover:text-[#1a365d] transition-colors">
            Retour au site
          </Link>
        </form>
      </div>
    );
  }

  const tabs = [
    { id: 'quotes', label: 'Devis', icon: FileText, count: stats.quotes },
    { id: 'contacts', label: 'Contacts', icon: Mail, count: stats.contacts },
    { id: 'chats', label: 'Chat', icon: MessageCircle, count: stats.chats },
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fb]" data-testid="admin-dashboard">
      {/* Top Bar */}
      <div className="bg-white border-b border-gray-100 px-4 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-gray-400 hover:text-[#1a365d] transition-colors" data-testid="admin-back-link">
            <ArrowLeft size={20} />
          </Link>
          <h1 className="font-bold text-lg text-[#1a365d]">Dashboard Admin</h1>
        </div>
        <Button
          data-testid="admin-refresh-btn"
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          className="rounded-full text-xs gap-1.5"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualiser
        </Button>
      </div>

      {/* Stat Cards */}
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6">
        <div className="grid grid-cols-3 gap-4 mb-6">
          {tabs.map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => { setTab(t.id); setSelectedSession(null); }}
              className={`rounded-xl p-4 text-left transition-all border ${
                tab === t.id
                  ? 'bg-[#1a365d] text-white border-[#1a365d] shadow-lg shadow-slate-700/10'
                  : 'bg-white text-gray-600 border-gray-100 hover:border-[#1a365d]/20'
              }`}
            >
              <t.icon size={20} className={tab === t.id ? 'text-blue-200' : 'text-gray-400'} />
              <p className={`text-2xl font-bold mt-2 ${tab === t.id ? 'text-white' : 'text-[#1a365d]'}`}>
                {t.count}
              </p>
              <p className={`text-xs mt-0.5 ${tab === t.id ? 'text-blue-200' : 'text-gray-400'}`}>
                {t.label}
              </p>
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'quotes' && (
          <div className="space-y-3" data-testid="admin-quotes-list">
            {quotes.length === 0 && <EmptyState text="Aucun devis pour le moment" />}
            {[...quotes].reverse().map((q, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={14} className="text-gray-400 shrink-0" />
                      <span className="font-semibold text-sm text-[#111] truncate">{q.name}</span>
                      <span className="bg-[#1a365d]/10 text-[#1a365d] text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0">
                        {q.project_label || q.project_type}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Mail size={12} />{q.email}</span>
                      {q.phone && <span className="flex items-center gap-1"><Phone size={12} />{q.phone}</span>}
                      {q.company && <span className="flex items-center gap-1"><Building2 size={12} />{q.company}</span>}
                    </div>
                    {q.message && <p className="text-sm text-gray-600 mt-2 line-clamp-2">{q.message}</p>}
                  </div>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1 shrink-0">
                    <Clock size={10} />{formatDate(q.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'contacts' && (
          <div className="space-y-3" data-testid="admin-contacts-list">
            {contacts.length === 0 && <EmptyState text="Aucun contact pour le moment" />}
            {[...contacts].reverse().map((c, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={14} className="text-gray-400 shrink-0" />
                      <span className="font-semibold text-sm text-[#111] truncate">{c.name}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Mail size={12} />{c.email}</span>
                      {c.company && <span className="flex items-center gap-1"><Building2 size={12} />{c.company}</span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-3">{c.message}</p>
                  </div>
                  <span className="text-[10px] text-gray-400 flex items-center gap-1 shrink-0">
                    <Clock size={10} />{formatDate(c.created_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'chats' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-chats-panel">
            {/* Sessions List */}
            <div className="lg:col-span-1 space-y-2">
              {chatSessions.length === 0 && <EmptyState text="Aucune conversation" />}
              {chatSessions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`chat-session-${i}`}
                  onClick={() => openSession(s)}
                  className={`w-full text-left rounded-xl border p-4 transition-all ${
                    selectedSession?.session_id === s.session_id
                      ? 'bg-[#1a365d] text-white border-[#1a365d]'
                      : 'bg-white border-gray-100 hover:border-[#1a365d]/20'
                  }`}
                >
                  <p className={`font-semibold text-sm truncate ${selectedSession?.session_id === s.session_id ? 'text-white' : 'text-[#111]'}`}>
                    {s.visitor_name || 'Visiteur'}
                  </p>
                  <p className={`text-xs mt-0.5 truncate ${selectedSession?.session_id === s.session_id ? 'text-blue-200' : 'text-gray-400'}`}>
                    {s.visitor_email || 'Email non fourni'}
                  </p>
                  <p className={`text-xs mt-1 truncate ${selectedSession?.session_id === s.session_id ? 'text-blue-100' : 'text-gray-500'}`}>
                    {s.last_message}
                  </p>
                </button>
              ))}
            </div>

            {/* Chat Messages */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 flex flex-col" style={{ minHeight: '400px' }}>
              {selectedSession ? (
                <>
                  <div className="px-5 py-3 border-b border-gray-100 shrink-0">
                    <p className="font-semibold text-sm text-[#111]">{selectedSession.visitor_name}</p>
                    <p className="text-xs text-gray-400">{selectedSession.visitor_email}</p>
                  </div>
                  <div ref={chatEndRef} className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="admin-chat-messages">
                    {chatMessages.map((m, i) => (
                      <div key={i} className={`flex ${m.sender === 'visitor' ? 'justify-start' : 'justify-end'}`}>
                        <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${
                          m.sender === 'visitor'
                            ? 'bg-gray-100 text-gray-800 rounded-bl-md'
                            : m.sender === 'admin'
                            ? 'bg-[#1a365d] text-white rounded-br-md'
                            : 'bg-amber-50 text-amber-800 border border-amber-200 rounded-br-md'
                        }`}>
                          {m.text}
                        </div>
                      </div>
                    ))}
                  </div>
                  <form onSubmit={sendReply} className="p-3 border-t border-gray-100 flex gap-2 shrink-0">
                    <Input
                      data-testid="admin-reply-input"
                      placeholder="Répondre..."
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      className="h-10 rounded-full border-gray-200 text-sm"
                    />
                    <Button
                      data-testid="admin-reply-send-btn"
                      type="submit"
                      disabled={!replyText.trim()}
                      size="icon"
                      className="h-10 w-10 rounded-full bg-[#1a365d] hover:bg-[#0f2440] shrink-0"
                    >
                      <Send size={16} />
                    </Button>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Sélectionnez une conversation
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
      <p className="text-gray-400 text-sm">{text}</p>
    </div>
  );
}
